import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShieldAlert, ArrowLeft, Mail, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";

// Landing page for anyone bounced off a gated partner route. ProtectedRoute navigates here with
// state describing WHAT was blocked and WHY, so the person can ask for the right thing rather
// than guessing after a toast that disappears.
export interface AccessDeniedState {
  from?: string;
  requiredRoles?: string[];
  requiredTeam?: string;
}

const SUPPORT_EMAIL = "info@capeberkshire.co.uk";

export default function AccessDenied() {
  const location = useLocation();
  const navigate = useNavigate();
  const { staffMember, user } = useAuth();
  const state = (location.state || {}) as AccessDeniedState;

  const attempted = state.from || "this page";
  const requirement = state.requiredTeam
    ? `Membership of the ${state.requiredTeam} team (or an admin role)`
    : state.requiredRoles?.length
      ? `One of these access levels: ${state.requiredRoles.join(", ")}`
      : "Additional permissions";

  const subject = encodeURIComponent(`Partner portal access request: ${attempted}`);
  const body = encodeURIComponent(
    [
      "Hello,",
      "",
      `I would like access to ${attempted} in the partner portal.`,
      "",
      `Name: ${staffMember ? `${staffMember.first_name} ${staffMember.last_name}` : ""}`,
      `Email: ${user?.email ?? ""}`,
      `Current team: ${staffMember?.team ?? "not set"}`,
      `Current access level: ${staffMember?.role_level ?? "not set"}`,
      `Required: ${requirement}`,
      "",
      "Reason for the request:",
      "",
    ].join("\n"),
  );

  return (
    <main className="w-full px-4 sm:px-6 lg:px-8 py-10">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-destructive/10 p-3">
            <ShieldAlert className="h-7 w-7 text-destructive" aria-hidden />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-primary">Access denied</h1>
            <p className="text-muted-foreground mt-1">
              Your account does not currently have permission to open{" "}
              <span className="font-medium text-foreground">{attempted}</span>.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">What this page needs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between gap-4 border-b pb-2">
              <span className="text-muted-foreground">Required</span>
              <span className="text-right font-medium">{requirement}</span>
            </div>
            <div className="flex justify-between gap-4 border-b pb-2">
              <span className="text-muted-foreground">Your team</span>
              <span className="text-right font-medium">{staffMember?.team || "Not set"}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Your access level</span>
              <span className="text-right font-medium">{staffMember?.role_level || "Not set"}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">How to request access</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <ol className="list-decimal pl-5 space-y-2">
              <li>
                Email an administrator with the page you need and why you need it. The button below
                pre-fills all of that for you.
              </li>
              <li>
                An admin updates your team or access level in Team Management, and can switch on the
                relevant navigation sections for your team.
              </li>
              <li>
                Once it is granted, sign out and back in (or refresh) so your new permissions load,
                then try the page again.
              </li>
            </ol>
            <div className="flex flex-wrap gap-3 pt-1">
              <Button asChild>
                <a href={`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`}>
                  <Mail className="h-4 w-4 mr-2" aria-hidden /> Request access by email
                </a>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/contact">
                  <LifeBuoy className="h-4 w-4 mr-2" aria-hidden /> Contact us
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden /> Go back
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/partner/dashboard">Return to dashboard</Link>
          </Button>
        </div>
      </div>
    </main>
  );
}
